import { inject, Injectable } from "@angular/core";
import {
	AmountType,
	CheckoutRouteName,
	PaymentMethod,
	CheckoutQueryParamName,
	CheckoutQueryParamsCollection
} from "@modules/checkout/entities/checkout.model";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { AccountService } from "@modules/account/services/account.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TooltipService } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { MasterdataService } from "@core-services/masterdata.service";

@Injectable({
	providedIn: "root"
})
export class CheckoutEntryHelperService {
	private readonly navigationService = inject(CheckoutNavigationService);
	private readonly tradableListService = inject(TradableListService);
	private readonly fiatWalletService = inject(FiatWalletService);
	private readonly accountService = inject(AccountService);
	private readonly walletListService = inject(TradableWalletListService);
	private readonly tooltipService = inject(TooltipService);
	private readonly fiatService = inject(FiatService);
	private readonly masterDataService = inject(MasterdataService);

	public onTradableSelect(
		assetId: string,
		queryParamObject?: Partial<CheckoutQueryParamsCollection>,
		dynamictermAndConditionActive: boolean = false
	): void {
		if (assetId) {
			const tradable = this.tradableListService.getItemById(assetId);
			const fiatId = queryParamObject?.[CheckoutQueryParamName.FiatId] || this.accountService.account?._fiat_id;
			const fiatWalletId = this.fiatWalletService.getWalletByFiatId(fiatId)?._id;
			const selectedAmountType = AmountType.fiat;
			const paymentMethod = PaymentMethod.fiatwallet;
			const paymentMethodId = "none";
			const amount = queryParamObject?.amount || 0;
			const HeaderSwitch = queryParamObject?.HeaderSwitch ?? false;

			let qry: Partial<CheckoutQueryParamsCollection> = {
				fiatId,
				assetId,
				fiatWalletId,
				selectedAmountType,
				paymentMethod,
				paymentMethodId,
				amount,
				HeaderSwitch,
				walletId: queryParamObject?.walletId
			};

			if (!qry.walletId) {
				let walletList = this.availableWallets("buy", tradable, this.fiatService.getItemById(fiatId));

				// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
				if (walletList.length > 1) {
					walletList = walletList.filter((w) => !w.isNew());
				}
				// If we still have more than 1 wallet, then we redirect to wallet select
				if (walletList.length > 1) {
					this.navigationService.gotoByNavigation(CheckoutRouteName.AssetWallets, {
						...qry,
						walletId: walletList[0]._id
					});
					return;
				}

				const walletId = walletList[0]?._id;

				qry = {
					...qry,
					walletId
				};
			}

			// Best 2.0 Terms
			if (
				tradable.getId() === TOKEN_BEST_ID &&
				!this.accountService.account.isBest2TermsAccepted() &&
				this.masterDataService.isBest2Enabled() &&
				!dynamictermAndConditionActive
			) {
				this.navigationService.gotoByNavigation(CheckoutRouteName.Best2Terms, qry);
				return;
			}
			this.navigationService.gotoByNavigation(CheckoutRouteName.AmountSelect, qry);
		}
	}

	public openCheckoutBuy(assetId?, queryParamObject?: Partial<CheckoutQueryParamsCollection>): void {
		if (assetId) {
			this.onTradableSelect(assetId, queryParamObject);
		} else {
			this.navigationService.gotoByNavigation(CheckoutRouteName.AssetSelect);
		}
	}

	private availableWallets(action: string, tradable: Tradable, fiat: Fiat): TradableWallet[] {
		return this.walletListService
			.getWalletsByTradableId(tradable.getId())
			.filter((wallet) => !this.tooltipService.isDisabled(action, tradable, fiat, wallet));
	}
}
